import React, { useState, useEffect } from 'react';
import { fmtDate, fmtDateShort } from '../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Plus, Calendar, ChevronDown, ChevronRight, Edit, Trash2, Building2, Clock, MapPin, Archive, Eye, Stethoscope, Heart, FileText, QrCode, Copy, ExternalLink, Download, Loader2, Timer, BarChart2 } from 'lucide-react';
import { stageBadges, entryConfirmedBadge, enteredBadge, notAcceptedBadge, withdrawnBadge, potentialSaleEntryBadge, showPotentialSaleEntry, showEntered, showNotAccepted, showWithdrawn } from './saleConstants';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import DeliverableMatrix from './DeliverableMatrix';
import AuctionResultsTab from './AuctionResultsTab';
import { toast } from 'sonner';
import api from '../../services/api';
import FunnelInsightsTable from './FunnelInsights';
import PostSaleMatrix from './PostSaleMatrix';
import VettingProcedureDetailDialog from './VettingProcedureDetailDialog';
import FilePreviewDialog from './FilePreviewDialog';

// All deadline fields with their display labels
const DEADLINE_FIELDS = [
  { key: 'entry_deadline',                        label: 'Entry',                        legacy: ['deadline_entry'] },
  { key: 'jockey_club_deadline',                  label: 'Jockey Club',                  legacy: ['deadline_jockey_club_papers'] },
  { key: 'coggins_deadline',                      label: 'Coggins',                      legacy: ['deadline_coggins'] },
  { key: 'eva_deadline',                          label: 'EVA',                          legacy: ['deadline_eva'] },
  { key: 'withdrawal_deadline',                   label: 'Withdrawal',                   legacy: ['deadline_withdrawal'] },
  { key: 'health_certificate_deadline',           label: 'Health Certificate' },
  { key: 'stallion_service_cert_deadline',        label: 'Stallion Service Certificate' },
  { key: 'authorized_agent_form_deadline',        label: 'Authorized Agent Form' },
  { key: 'breeding_soundness_exam_deadline',      label: 'Breeding Soundness Exam' },
  { key: 'sales_company_contract_deadline',       label: 'Sales Company Contract' },
  { key: 'pedigree_proof_deadline',               label: 'Pedigree Proof' },
  { key: 'pedigree_proof_property_line_deadline', label: 'Pedigree Proof (Property Line)' },
  { key: 'first_500_entry_fee_deadline',          label: 'First $500 Entry Fee' },
  { key: 'wish_list_deadline',                    label: 'Wish List' },
  { key: 'placements_released_deadline',          label: 'Placements Released' },
  { key: 'request_session_swaps_deadline',        label: 'Request Session Swaps' },
];

const getDeadlineValue = (sale, field) => {
  if (sale[field.key]) return sale[field.key];
  for (const alias of (field.legacy || [])) {
    if (sale[alias]) return sale[alias];
  }
  return null;
};

const SaleCard = ({
  sale,
  sessions: saleSessions,
  opportunities: saleOpportunities,
  saleOwnerDeliverables,
  opportunityOwners,
  ownerEntities,
  isExpanded,
  onToggleExpand,
  deadlinesVisible,
  onToggleDeadlines,
  viewHistoric,
  onArchive,
  onUnarchive,
  onDelete,
  isTenantAdmin,
  isCardRunner,
  isSaleClerk,
  isViewOnly,
  onCreateSession,
  onEditSession,
  onRefresh,
}) => {
  const navigate = useNavigate();

  const isDigitalSale = !!sale.is_digital_sale;
  const hasNoSessions = saleSessions.length === 0;
  const hasIncompleteSessions = saleSessions.some(s => !s.barn || !s.time);
  const showWarning = !isDigitalSale && (hasNoSessions || hasIncompleteSessions);


  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const saleStartDate = sale.sale_start_date || sale.start_date;
  const saleEndDate = sale.sale_end_date || sale.end_date;
  // Compare dates only (strip time) to match categorizeSales logic
  const startDay = saleStartDate ? new Date(new Date(saleStartDate).getFullYear(), new Date(saleStartDate).getMonth(), new Date(saleStartDate).getDate()) : null;
  const endDay = saleEndDate ? new Date(new Date(saleEndDate).getFullYear(), new Date(saleEndDate).getMonth(), new Date(saleEndDate).getDate()) : startDay;
  if (endDay) endDay.setHours(23, 59, 59, 999);

  // Effective start includes earliest ship-in date (matching categorizeSales)
  let effectiveStart = startDay ? new Date(startDay) : null;
  saleSessions.forEach(s => {
    if (s.ship_in_date) {
      const shipDate = new Date(s.ship_in_date);
      const shipDay = new Date(shipDate.getFullYear(), shipDate.getMonth(), shipDate.getDate());
      if (!effectiveStart || shipDay < effectiveStart) effectiveStart = shipDay;
    }
  });

  const isLiveSale = effectiveStart && endDay && today >= effectiveStart && today <= endDay;
  const isPostSale = endDay && today > endDay;

  return (
    <Card className="mb-4">
      <div style={{ padding: '10px 12px 6px 12px' }}>
        <div className="flex items-start gap-2">
          <Button variant="ghost" size="icon" className="shrink-0 mt-0.5" onClick={onToggleExpand}>
            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <CardTitle className="text-base leading-tight">{sale.sale_name}</CardTitle>
              {showWarning && <Badge variant="destructive" className="text-xs">{hasNoSessions ? 'No Sessions' : 'Incomplete Sessions'}</Badge>}
              {sale.archived && <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">Archived</Badge>}
            </div>
            {/* Company + Location */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground mt-1">
              {sale.sale_company && (
                <span className="flex items-center gap-1 capitalize">
                  <Building2 className="w-3 h-3" />{sale.sale_company.replace('_', ' ')}
                </span>
              )}
              {sale.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />{sale.location}
                </span>
              )}
            </div>
            {/* Sale dates */}
            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
              <Calendar className="w-3 h-3" />
              <span>
                {fmtDateShort(sale.start_date || sale.sale_start_date)}
                {(sale.end_date || sale.sale_end_date) && ` – ${fmtDate(sale.end_date || sale.sale_end_date)}`}
              </span>
            </div>
            {/* Undertack dates */}
            {sale.is_undertack_show && (sale.under_tack_start_date || sale.under_tack_end_date) && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                <Timer className="w-3 h-3" />
                <span>
                  Undertack:{' '}
                  {sale.under_tack_start_date && fmtDateShort(sale.under_tack_start_date)}
                  {sale.under_tack_start_date && sale.under_tack_end_date && ' – '}
                  {sale.under_tack_end_date && fmtDateShort(sale.under_tack_end_date)}
                </span>
              </div>
            )}
            {/* Action buttons — mobile only */}
            <div className="flex sm:hidden items-center gap-2 mt-1 flex-wrap">
              {viewHistoric && sale.archived && isTenantAdmin && (
                <Button variant="outline" size="sm" style={{ height: '28px', minHeight: '28px', fontSize: '12px', padding: '0 8px' }}
                  className="border-green-600 text-green-600 hover:bg-green-50"
                  onClick={(e) => { e.stopPropagation(); onUnarchive?.(sale); }}>Unarchive</Button>
              )}
              {!viewHistoric && !sale.archived && isTenantAdmin && (
                <Button variant="outline" size="sm" style={{ height: '28px', minHeight: '28px', fontSize: '12px', padding: '0 8px' }}
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                  onClick={(e) => { e.stopPropagation(); onArchive(sale); }}>
                  <Archive className="w-3 h-3 mr-1" />Archive</Button>
              )}
              {isTenantAdmin && (
                <Button variant="ghost" size="sm" style={{ height: '28px', minHeight: '28px', fontSize: '12px', padding: '0 8px' }}
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => onDelete(sale.id)}>
                  <Trash2 className="w-3 h-3 mr-1" />Delete</Button>
              )}
            </div>
          </div>
          {/* Action buttons — desktop only */}
          <div className="hidden sm:flex items-center gap-2 shrink-0">
            {viewHistoric && sale.archived && isTenantAdmin && (
              <Button variant="outline" size="sm" style={{ height: '28px', minHeight: '28px', fontSize: '12px', padding: '0 8px' }}
                className="border-green-600 text-green-600 hover:bg-green-50"
                onClick={(e) => { e.stopPropagation(); onUnarchive?.(sale); }}>Unarchive</Button>
            )}
            {!viewHistoric && !sale.archived && isTenantAdmin && (
              <Button variant="outline" size="sm" style={{ height: '28px', minHeight: '28px', fontSize: '12px', padding: '0 8px' }}
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
                onClick={(e) => { e.stopPropagation(); onArchive(sale); }}>
                <Archive className="w-3 h-3 mr-1" />Archive</Button>
            )}
            {isTenantAdmin && (
              <Button variant="ghost" size="sm" style={{ height: '28px', minHeight: '28px', fontSize: '12px', padding: '0 8px' }}
                className="text-muted-foreground hover:text-destructive"
                onClick={() => onDelete(sale.id)}>
                <Trash2 className="w-3 h-3 mr-1" />Delete</Button>
            )}
          </div>
        </div>
      </div>
      {isExpanded && (
        <CardContent style={{ paddingTop: 0, paddingLeft: '12px', paddingRight: '12px', paddingBottom: '12px' }}>

          {/* Deadlines */}
          <DeadlinesSection
            sale={sale}
            deadlinesVisible={deadlinesVisible}
            onToggleDeadlines={onToggleDeadlines}
          />

          {/* Tabs - Different for Live/Post vs Pre-Sale */}
          {(isLiveSale || isPostSale) ? (
            <LivePostSaleTabs
              sale={sale}
              saleSessions={saleSessions}
              saleOpportunities={saleOpportunities}
              saleOwnerDeliverables={saleOwnerDeliverables}
              opportunityOwners={opportunityOwners}
              ownerEntities={ownerEntities}
              onCreateSession={onCreateSession}
              onEditSession={onEditSession}
              navigate={navigate}
              onRefresh={onRefresh}
              isCardRunner={isCardRunner}
              isSaleClerk={isSaleClerk}
              isDigitalSale={isDigitalSale}
              isViewOnly={isViewOnly}
            />
          ) : (
            <PreSaleTabs
              sale={sale}
              saleSessions={saleSessions}
              saleOpportunities={saleOpportunities}
              saleOwnerDeliverables={saleOwnerDeliverables}
              opportunityOwners={opportunityOwners}
              ownerEntities={ownerEntities}
              onCreateSession={onCreateSession}
              onEditSession={onEditSession}
              navigate={navigate}
            />
          )}
        </CardContent>
      )}
    </Card>
  );
};

/* --- Sub-components --- */

const DeadlinesSection = ({ sale, deadlinesVisible, onToggleDeadlines }) => {
  const showDeadlines = deadlinesVisible !== false;
  const activeDeadlines = DEADLINE_FIELDS
    .map(f => ({ label: f.label, date: getDeadlineValue(sale, f) }))
    .filter(d => d.date);

  return (
    <div className="mb-4 pb-4 border-b">
      <div className="flex items-center gap-2 mb-2">
        <p className="text-xs font-medium text-muted-foreground">DEADLINES:</p>
        <Button variant="ghost" size="sm" onClick={onToggleDeadlines} className="h-5 px-1.5 text-xs text-muted-foreground">
          {showDeadlines ? 'Hide' : 'Show'}
        </Button>
      </div>
      {showDeadlines && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: '4px 24px' }} className="text-xs">
          {activeDeadlines.length === 0 ? (
            <div className="col-span-2 text-muted-foreground italic">No deadlines set</div>
          ) : (
            activeDeadlines.map(d => (
              <div key={d.label}>
                <span className="text-muted-foreground">{d.label}: </span>
                <span>{fmtDateShort(d.date)}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const SessionsTable = ({ sale, saleSessions, onCreateSession, onEditSession }) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const saleStartDate = sale.sale_start_date || sale.start_date;
  const saleEndDate = sale.sale_end_date || sale.end_date;
  // Compare dates only (strip time)
  const startDay = saleStartDate ? new Date(new Date(saleStartDate).getFullYear(), new Date(saleStartDate).getMonth(), new Date(saleStartDate).getDate()) : null;
  const saleHasStarted = startDay && now >= startDay;
  const saleHasEnded = saleEndDate && new Date(saleEndDate) < now;

  const canAddSession = !sale.archived && !saleHasStarted;

  return (
    <div className="space-y-4">
      <Button
        size="sm"
        onClick={() => onCreateSession(sale)}
        disabled={!canAddSession}
        className="bg-primary hover:bg-primary/90 text-white"
        title={!canAddSession ? (
          sale.archived ? 'Cannot add sessions to archived sale' :
          saleHasEnded ? 'Cannot add sessions after sale has ended' :
          'Cannot add sessions once sale has started'
        ) : ''}
      >
        <Plus className="w-4 h-4 mr-2" />
        Add Session
      </Button>

      {saleSessions.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">
          No sessions created yet.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Session Name</TableHead>
              <TableHead>Session Date</TableHead>
              <TableHead>Ship In</TableHead>
              <TableHead>Barn</TableHead>
              <TableHead>Ship In Time</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {saleSessions
              .sort((a, b) => new Date(a.date) - new Date(b.date))
              .map((session) => {
                const canEdit = !sale.archived && !saleHasEnded;
                return (
                  <TableRow key={session.id}>
                    <TableCell className="font-medium">{session.name}</TableCell>
                    <TableCell>
                      {fmtDate(session.date)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Calendar className="w-3 h-3 text-muted-foreground" />
                        {fmtDate(session.ship_in_date)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Building2 className="w-3 h-3 text-muted-foreground" />
                        {session.barn || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Clock className="w-3 h-3 text-muted-foreground" />
                        {session.time ? (() => { const [h,m] = session.time.split(':').map(Number); if (isNaN(h)) return session.time; const hr = h === 0 ? 12 : h > 12 ? h - 12 : h; return `${hr}:${String(m||0).padStart(2,'0')} ${h < 12 ? 'AM' : 'PM'}`; })() : '-'}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {canEdit && (
                        <Button variant="ghost" size="icon" onClick={() => onEditSession(sale, session)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

const OpportunitiesTable = ({ saleOpportunities, saleSessions, opportunityOwners, navigate, variant = 'presale', sale }) => {
  // Always display in ascending hip number order
  const sortedOpps = [...saleOpportunities].sort(
    (a, b) => (parseInt(a.hip_number) || 9999) - (parseInt(b.hip_number) || 9999)
  );
  const [previewFile, setPreviewFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [breezeMap, setBreezeMap] = useState({});
  const [etaMap, setEtaMap] = useState({});  // hip → estimated_arrival ISO string

  // Fetch breeze times for the sale if it has an undertack show
  useEffect(() => {
    if (sale?.is_undertack_show && saleOpportunities.length > 0) {
      const saleId = saleOpportunities[0]?.sale_id;
      if (saleId) {
        api.get(`/breeze-times?sale_id=${saleId}`)
          .then(r => setBreezeMap(r.data || {}))
          .catch(() => {});
      }
    }
  }, [sale?.is_undertack_show, saleOpportunities]);

  // Fetch ETA estimates — refresh every 5 minutes so new estimates appear automatically
  useEffect(() => {
    const liveSaleId = saleOpportunities.find(o => o.stage === 'live_sale')?.sale_id
      || saleOpportunities[0]?.sale_id;
    if (!liveSaleId) return;
    const fetchEtas = () => {
      api.get(`/sale-window/state/${liveSaleId}`)
        .then(r => setEtaMap(r.data?.estimates || {}))
        .catch(() => {});
    };
    fetchEtas();
    const interval = setInterval(fetchEtas, 2 * 60 * 1000); // every 2 min — matches Playwright scrape interval
    return () => clearInterval(interval);
  }, [saleOpportunities]);

  // Determine if the sale itself is currently live based on dates
  const todayStr = new Date().toLocaleDateString('sv');
  const saleStartStr = sale?.sale_start_date ? new Date(sale.sale_start_date).toLocaleDateString('sv') : null;
  const saleEndStr   = sale?.sale_end_date   ? new Date(sale.sale_end_date).toLocaleDateString('sv')   : null;
  // Also consider ship-in dates from sessions (same logic as SaleOpportunityDetailPage)
  let shipInStr = null;
  for (const s of (Array.isArray(saleSessions) ? saleSessions : [])) {
    const d = s.ship_in_date ? new Date(s.ship_in_date).toLocaleDateString('sv') : null;
    if (d && (!shipInStr || d < shipInStr)) shipInStr = d;
  }
  const liveStartStr = shipInStr && saleStartStr ? (shipInStr < saleStartStr ? shipInStr : saleStartStr) : (shipInStr || saleStartStr);
  // isSaleLive: date-based OR sale has a table link configured (actively tracked)
  const isSaleLive = !!(
    (liveStartStr && todayStr >= liveStartStr && (!saleEndStr || todayStr <= saleEndStr))
    || sale?.sale_table_link
  );

  // Derive isDigitalSale from the sale prop (mirrors the parent SaleCard logic)
  const isDigitalSale = !!sale?.is_digital_sale;

  const openPreview = (e, deliverable, label) => {
    e.stopPropagation();
    setPreviewFile({ ...deliverable, deliverable_type_label: label });
    setPreviewOpen(true);
  };

  if (saleOpportunities.length === 0) {
    return <p className="text-sm text-muted-foreground italic py-4">No horses entered in this sale</p>;
  }

  if (variant === 'live') {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Hip #</TableHead>
            <TableHead>Horse System Name</TableHead>
            <TableHead>Session Date</TableHead>
            <TableHead>Total Views</TableHead>
            <TableHead>Dam</TableHead>
            <TableHead>Sire</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedOpps.map(opp => {
            const session = saleSessions.find(s => s.id === opp.session_id);
            return (
              <TableRow
                key={opp.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => navigate(`/sales/opportunities/${opp.id}`)}
              >
                <TableCell>#{opp.hip_number || 'TBD'}</TableCell>
                <TableCell className="font-medium">{opp.horse_display_name || opp.opportunity_name?.split(' - ')[1] || '-'}</TableCell>
                <TableCell>{session?.date ? fmtDateShort(session.date) : '-'}</TableCell>
                <TableCell>{opp.total_views || 0}</TableCell>
                <TableCell>{opp.dam_name || '-'}</TableCell>
                <TableCell>{opp.sire_name || '-'}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  }

  // Pre-sale variant
  const showUT = !!sale?.is_undertack_show;
  return (
    <>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="text-xs">Hip #</TableHead>
          <TableHead className="text-xs">Horse</TableHead>
          <TableHead className="text-xs w-[140px]"></TableHead>
          {showUT && <TableHead className="text-xs">UT Time</TableHead>}
          <TableHead className="text-xs">Session</TableHead>
          {!isDigitalSale && (
            <TableHead className="text-xs">
              <span className="flex items-center gap-1">
                Est. Ring Time
                <span className="text-[8px] font-bold uppercase tracking-wide bg-amber-200 text-amber-800 px-1 py-0.5 rounded">Beta</span>
              </span>
            </TableHead>
          )}
          <TableHead className="text-xs">Result</TableHead>
          <TableHead className="text-xs">Owner(s)</TableHead>
          <TableHead className="text-xs">Pre-Sale Stage</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedOpps.map(opp => {
          const sessionName = saleSessions.find(s => s.id === opp.session_id)?.name || '-';
          const presaleStageMap = {
            'presale_create': stageBadges.presale_create,
            'presale_confirm': stageBadges.presale_confirm,
            'presale_deliverables': stageBadges.presale_deliverables,
            'presale_ready': stageBadges.presale_ready,
            'live_sale': stageBadges.presale_ready,
            'post_sale': stageBadges.presale_ready
          };
          const badge = presaleStageMap[opp.stage] || null;
          const showEntryConfirmed = opp.entry_status === 'confirmed';
          const showPotential = showPotentialSaleEntry(opp.entry_status);          const ownerNames = opportunityOwners[opp.id] || '-';
          const deliverables = opp.deliverables || [];
          const pedigreeDeliverable = deliverables.find(d =>
            (d.deliverable_type_horse === 'catalogue_page' || d.deliverable_type === 'catalogue_page') && d.file_url
          );
          const vetDeliverable = deliverables.find(d =>
            (d.deliverable_type_horse === 'vet_report' || d.deliverable_type === 'vet_report') && d.file_url
          );
          const utTime = breezeMap[opp.id]?.ut_time || '';

          return (
            <TableRow
              key={opp.id}
              className="cursor-pointer hover:bg-muted/50"
              onDoubleClick={() => navigate(`/sales/opportunities/${opp.id}`)}
            >
              <TableCell className="text-xs">#{opp.hip_number || 'TBD'}</TableCell>
              <TableCell className="text-xs font-medium">{opp.horse_display_name || opp.opportunity_name?.split(' - ')[1] || '-'}</TableCell>
              <TableCell onClick={e => e.stopPropagation()}>
                <div className="flex items-center gap-1">
                  {pedigreeDeliverable && (
                    <Button size="sm" variant="outline"
                      className="h-6 text-[11px] px-1.5 gap-0.5 border-primary/30 text-primary hover:bg-primary hover:text-white"
                      onClick={e => openPreview(e, pedigreeDeliverable, 'Catalogue Page')}
                      data-testid="list-pedigree-btn">
                      <FileText className="w-3 h-3" /> Pedigree
                    </Button>
                  )}
                  {vetDeliverable && (
                    <Button size="sm" variant="outline"
                      className="h-6 text-[11px] px-1.5 gap-0.5 border-primary/30 text-primary hover:bg-primary hover:text-white"
                      onClick={e => openPreview(e, vetDeliverable, 'Vet Report')}
                      data-testid="list-vet-btn">
                      <Stethoscope className="w-3 h-3" /> Vet Report
                    </Button>
                  )}
                </div>
              </TableCell>
              {showUT && <TableCell className="text-xs font-mono">{utTime || <span className="text-muted-foreground">—</span>}</TableCell>}
              <TableCell className="text-xs">{sessionName}</TableCell>
              {!isDigitalSale && (
              <TableCell className="text-xs">
                {(() => {
                  const hipKey = String(opp.hip_number);
                  const eta = etaMap[hipKey] || etaMap[parseFloat(hipKey)?.toString()];
                  if (!isSaleLive || !opp.hip_number) return <span className="text-muted-foreground">—</span>;
                  if (opp.result === 'withdrawn' || opp.entry_status === 'withdrawn' || opp.result === 'out' || opp.entry_status === 'out') return <span className="text-muted-foreground">—</span>;
                  if (!eta?.estimated_arrival) return <span className="text-amber-400 font-medium">TBD</span>;
                  const timeStr = new Date(eta.estimated_arrival).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                  const isActual = eta.phase === 'actual';
                  const isPast   = !isActual && new Date(eta.estimated_arrival) < new Date();
                  if (isActual) return <span className="font-semibold text-green-700" title="Actual ring time">{timeStr}</span>;
                  if (isPast)   return <span className="font-medium text-muted-foreground" title="Estimated — horse has likely gone through">~{timeStr}</span>;
                  return <span className="font-semibold text-amber-700">{timeStr}</span>;
                })()}
              </TableCell>
              )}
              <TableCell className="text-xs">
                {opp.result && opp.result !== 'pending' ? (
                  <Badge className={opp.result === 'sold' ? 'bg-green-100 text-green-700 text-xs' : opp.result === 'rna' ? 'bg-yellow-100 text-yellow-700 text-xs' : opp.result === 'withdrawn' ? 'bg-red-100 text-red-700 text-xs' : 'bg-gray-100 text-gray-700 text-xs'}>
                    {opp.result.toUpperCase()}
                  </Badge>
                ) : <span className="text-muted-foreground">Pending</span>}
              </TableCell>
              <TableCell className="text-xs">{ownerNames}</TableCell>
              <TableCell className="text-xs">
                {badge ? <Badge className={badge.className}>{badge.label}</Badge> : null}
                {showPotential && <Badge className={`${potentialSaleEntryBadge.className} text-xs`}>{potentialSaleEntryBadge.label}</Badge>}
                {showEntered(opp.entry_status) && <Badge className={`${enteredBadge.className} text-xs`}>{enteredBadge.label}</Badge>}
                {showNotAccepted(opp.entry_status) && <Badge className={`${notAcceptedBadge.className} text-xs`}>{notAcceptedBadge.label}</Badge>}
                {showWithdrawn(opp.entry_status) && <Badge className={`${withdrawnBadge.className} text-xs`}>{withdrawnBadge.label}</Badge>}
                {showEntryConfirmed && <Badge className={`${entryConfirmedBadge.className} text-xs`}>{entryConfirmedBadge.label}</Badge>}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
    {previewFile && (
      <FilePreviewDialog
        open={previewOpen}
        onClose={() => { setPreviewOpen(false); setPreviewFile(null); }}
        file={previewFile}
      />
    )}
    </>
  );
};

const SaleActivityHighlights = ({ completedViewings, saleOpportunities, vettingProcs }) => {
  // Count completed views per opportunity
  const viewCounts = {};
  (completedViewings || []).forEach(v => {
    const oppId = v.sale_opportunity_id;
    if (oppId) viewCounts[oppId] = (viewCounts[oppId] || 0) + 1;
  });

  // Count vetting from fresh vettingProcs (not stale opp.total_vetting_procedures)
  const vettingCounts = {};
  (vettingProcs || []).forEach(v => {
    const oppId = v.sale_opportunity_id;
    if (oppId) vettingCounts[oppId] = (vettingCounts[oppId] || 0) + 1;
  });

  const topViewsOpp = saleOpportunities.reduce((best, opp) =>
    (viewCounts[opp.id] || 0) > (viewCounts[best?.id] || 0) ? opp : best
  , saleOpportunities[0] || {});

  const topVetting = saleOpportunities.reduce((best, opp) =>
    (vettingCounts[opp.id] || 0) > (vettingCounts[best?.id] || 0) ? opp : best
  , saleOpportunities[0] || {});

  const topViewsCount = viewCounts[topViewsOpp?.id] || 0;
  const topVettingCount = vettingCounts[topVetting?.id] || 0;

  const horseName = (opp) => opp?.horse_display_name || opp?.opportunity_name?.split(' - ')[1] || '-';

  if (saleOpportunities.length === 0) return null;

  return (
    <div className="flex items-center gap-3 p-3 border rounded bg-muted/30 mb-4">
      <Eye className="w-5 h-5 text-blue-600 shrink-0" />
      <div className="text-sm">
        {topViewsCount > 0 ? (
          <>
            <span className="text-muted-foreground">Most Views: </span>
            <span className="font-medium">{horseName(topViewsOpp)}</span>
            <span className="text-muted-foreground text-xs ml-1">({topViewsCount})</span>
          </>
        ) : (
          <span className="text-muted-foreground italic text-xs">No views recorded yet</span>
        )}
      </div>
    </div>
  );
};

const RecentActivityTables = ({ saleId, saleOpportunities, sale }) => {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [viewings, setViewings] = useState([]);
  const [vettingProcs, setVettingProcs] = useState([]);
  const [selectedVetting, setSelectedVetting] = useState(null);
  const [digitalMap, setDigitalMap] = useState({});

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [cardsRes, vettingRes, viewingsRes] = await Promise.all([
          api.get(`/sale-cards?sale_id=${saleId}`),
          api.get(`/vetting-procedures?sale_id=${saleId}`),
          api.get(`/sale-cards/all-viewings?sale_id=${saleId}`),
        ]);
        setVettingProcs(vettingRes.data || []);
        setCards(cardsRes.data || []);
        // All viewings for the sale in one shot — no per-card N+1, no 10-card limit
        const allViewings = (viewingsRes.data || []);
        allViewings.sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
        setViewings(allViewings);
      } catch { /* ignore */ }

      // Fetch digital metrics if this is a digital sale
      if (sale?.is_digital_sale) {
        try {
          const dmRes = await api.get(`/digital-metrics?sale_id=${saleId}`);
          setDigitalMap(dmRes.data || {});
        } catch { /* ignore */ }
      }
    };
    fetchAll();
    // Poll every 2 minutes so Most Views and Most Vetting stay live
    const interval = setInterval(fetchAll, 2 * 60 * 1000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saleId]);

  const completedViewings = viewings.filter(v => v.status === 'completed');
  const recentViews = completedViewings.slice(0, 5);
  const recentVetting = vettingProcs.slice(0, 5);

  // Virtual enquiries (digital sales)
  const virtualCards = cards.filter(c => c.card_type === 'virtual_enquiry');

  return (
    <>
    <SaleActivityHighlights completedViewings={completedViewings} saleOpportunities={saleOpportunities || []} vettingProcs={vettingProcs} />

    {/* Digital Metrics Summary — shown for digital sales */}
    {sale?.is_digital_sale && (
      <div className="mb-4">
        <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><BarChart2 className="w-4 h-4 text-primary" /> Digital Metrics</h3>
        {(saleOpportunities || []).filter(opp => digitalMap[opp.id]).length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No digital metrics recorded yet. Open an opportunity to enter metrics.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Hip #</TableHead>
                <TableHead className="text-xs">Horse</TableHead>
                <TableHead className="text-xs"><Eye className="w-3 h-3 inline mr-1" />Page Views</TableHead>
                <TableHead className="text-xs"><FileText className="w-3 h-3 inline mr-1" />Media</TableHead>
                <TableHead className="text-xs"><Heart className="w-3 h-3 inline mr-1" />Watchlist</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(saleOpportunities || []).filter(opp => digitalMap[opp.id]).map(opp => {
                const dm = digitalMap[opp.id] || {};
                return (
                  <TableRow key={opp.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/sales/opportunities/${opp.id}?tab=digital-metrics`)}>
                    <TableCell className="text-xs font-medium">#{opp.hip_number || 'TBD'}</TableCell>
                    <TableCell className="text-xs">{opp.horse_display_name || opp.opportunity_name?.split(' - ')[1] || '-'}</TableCell>
                    <TableCell className="text-xs font-mono">{dm.catalog_page_views ?? '—'}</TableCell>
                    <TableCell className="text-xs font-mono">{dm.media_engagement ?? '—'}</TableCell>
                    <TableCell className="text-xs font-mono">{dm.watchlist_adds ?? '—'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    )}

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Recent Views */}
      <div>
        <h3 className="text-sm font-semibold mb-2">Recent Views</h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Hip #</TableHead>
              <TableHead>Horse</TableHead>
              <TableHead>Viewer</TableHead>
              <TableHead>Liked</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {recentViews.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-4">No views recorded yet</TableCell></TableRow>
            ) : (
              recentViews.map(v => (
                <TableRow key={v.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/sales/${saleId}/cards/${v.card_id}`)}>
                  <TableCell className="text-xs font-medium">#{v.hip_number || 'TBD'}</TableCell>
                  <TableCell className="text-xs">{v.horse_name || '-'}</TableCell>
                  <TableCell className="text-xs">{v.viewer_name || '-'}</TableCell>
                  <TableCell>{v.liked ? <Heart className="w-3 h-3 text-red-500 fill-current" /> : <span className="text-xs text-muted-foreground">-</span>}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Most Vetting — between Recent Views and Recent Vetting */}
      {(() => {
        const vettingCountByOpp = {};
        vettingProcs.forEach(v => {
          if (v.sale_opportunity_id) vettingCountByOpp[v.sale_opportunity_id] = (vettingCountByOpp[v.sale_opportunity_id] || 0) + 1;
        });
        const topVetting = (saleOpportunities || []).reduce((best, opp) =>
          (vettingCountByOpp[opp.id] || 0) > (vettingCountByOpp[best?.id] || 0) ? opp : best
        , null);
        const topVettingCount = topVetting ? (vettingCountByOpp[topVetting.id] || 0) : 0;
        return (
          <div className="flex items-center gap-3 p-3 border rounded bg-muted/30 md:col-span-2">
            <Stethoscope className="w-5 h-5 text-green-600 shrink-0" />
            <div className="text-sm">
              {topVettingCount > 0 ? (
                <>
                  <span className="text-muted-foreground">Most Vetting: </span>
                  <span className="font-medium">{topVetting.opportunity_name?.split(' - ')[1] || topVetting.opportunity_name || `Hip #${topVetting.hip_number}`}</span>
                  <span className="text-muted-foreground text-xs ml-1">({topVettingCount})</span>
                </>
              ) : (
                <span className="text-muted-foreground italic text-xs">No vetting procedures recorded yet</span>
              )}
            </div>
          </div>
        );
      })()}

      {/* Recent Vetting Procedures */}
      <div>
        <h3 className="text-sm font-semibold mb-2">Recent Vetting Procedures</h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Hip #</TableHead>
              <TableHead>Horse</TableHead>
              <TableHead>Vet</TableHead>
              <TableHead>Procedure</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {recentVetting.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-4">No vetting procedures recorded yet</TableCell></TableRow>
            ) : (
              recentVetting.map(p => (
                <TableRow key={p.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedVetting(p)}>
                  <TableCell className="text-xs font-medium">#{p.hip_number || 'TBD'}</TableCell>
                  <TableCell className="text-xs">{p.horse_name || '-'}</TableCell>
                  <TableCell className="text-xs">{p.vet_name || '-'}</TableCell>
                  <TableCell className="text-xs flex items-center gap-1">
                    {p.procedure?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '-'}
                    {p.viewed_vet_report && <FileText className="w-3 h-3 text-blue-500 inline shrink-0" />}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
    <VettingProcedureDetailDialog
      procedure={selectedVetting}
      onClose={() => setSelectedVetting(null)}
      onSaved={() => setSelectedVetting(null)}
    />
    </>
  );
};

const LivePostSaleTabs = ({
  sale, saleSessions, saleOpportunities, saleOwnerDeliverables,
  opportunityOwners, ownerEntities, onCreateSession, onEditSession, navigate, onRefresh,
  isCardRunner, isSaleClerk, isViewOnly, isDigitalSale
}) => {
  const hasNonPendingResults = saleOpportunities.some(o => o.result && o.result !== 'pending');
  const isRestricted = isCardRunner || isSaleClerk;
  const defaultTab = isSaleClerk ? 'auction-results' : 'sale-activity';

  // Active-only filter for Sale Activity tab (Most Views, Most Vettings, Funnel Insights)
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const _inactiveResults = new Set(['sold', 'rna', 'withdrawn']);
  const activeOnlyOpps = showActiveOnly
    ? (saleOpportunities || []).filter(
        opp => !_inactiveResults.has(opp.result) && !_inactiveResults.has(opp.entry_status)
      )
    : (saleOpportunities || []);

  const visibleTabs = [
    !isSaleClerk && 'sale-activity',
    !isCardRunner && 'auction-results',
    !isRestricted && hasNonPendingResults && 'post-sale-actions',
    !isRestricted && 'opportunities',
    !isRestricted && !isDigitalSale && 'sessions',
    !isRestricted && 'deliverables',
  ].filter(Boolean);

  // Scrollable flex tabs — works on all screen sizes
  const tabListClass = 'flex w-full overflow-x-auto h-auto flex-nowrap scrollbar-none';

  return (
  <Tabs defaultValue={defaultTab} className="mt-4">
    <TabsList className={tabListClass}>
      {!isSaleClerk && <TabsTrigger value="sale-activity">Sale Activity</TabsTrigger>}
      {!isCardRunner && <TabsTrigger value="auction-results">Results</TabsTrigger>}
      {!isRestricted && hasNonPendingResults && <TabsTrigger value="post-sale-actions">Post-Sale Actions</TabsTrigger>}
      {!isRestricted && <TabsTrigger value="opportunities">Sale Opportunities</TabsTrigger>}
      {!isRestricted && !isDigitalSale && <TabsTrigger value="sessions">Sessions</TabsTrigger>}
      {!isRestricted && <TabsTrigger value="deliverables">Deliverable Matrix</TabsTrigger>}
    </TabsList>

    {!isSaleClerk && (
    <TabsContent value="sale-activity">
      <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2 mb-4">
        <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={() => navigate(`/sales/${sale.id}/card-management?name=${encodeURIComponent(sale.sale_name)}`)}>
          <Edit className="w-4 h-4 mr-2" />Card Management
        </Button>
        <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={() => navigate(`/sales/${sale.id}/vetting-procedures?name=${encodeURIComponent(sale.sale_name)}`)}>
          <Stethoscope className="w-4 h-4 mr-2" />Vetting Procedures
        </Button>
        {!isRestricted && (
          <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={() => navigate(`/sales/${sale.id}/viewer-summary?name=${encodeURIComponent(sale.sale_name)}`)}>
            <Eye className="w-4 h-4 mr-2" />Viewer Summary
          </Button>
        )}
        {!isRestricted && (
          <KioskLinkButton saleId={sale.id} />
        )}
      </div>
    {!isRestricted && (
        <>
          <div className="flex items-center justify-end gap-2 mb-3">
            <span className="text-xs text-muted-foreground">Active horses only</span>
            <Switch
              checked={showActiveOnly}
              onCheckedChange={setShowActiveOnly}
              data-testid="sale-activity-active-toggle"
            />
          </div>
          <RecentActivityTables saleId={sale.id} saleOpportunities={activeOnlyOpps} sale={sale} />
          <FunnelInsightsTable saleId={sale.id} activeOnly={showActiveOnly} />
        </>
      )}
      {isCardRunner && (
        <RecentActivityTables saleId={sale.id} saleOpportunities={saleOpportunities} sale={sale} />
      )}
    </TabsContent>
    )}

    {!isRestricted && (
    <TabsContent value="opportunities">
      <OpportunitiesTable
        saleOpportunities={saleOpportunities}
        saleSessions={saleSessions}
        opportunityOwners={opportunityOwners}
        navigate={navigate}
        variant="presale"
        sale={sale}
      />
    </TabsContent>
    )}

    {!isCardRunner && (
    <TabsContent value="auction-results">
      <AuctionResultsTab
        saleOpportunities={saleOpportunities}
        saleSessions={saleSessions}
        onRefresh={onRefresh}
      />
    </TabsContent>
    )}

    {!isRestricted && (
    <TabsContent value="post-sale-actions">
      <PostSaleMatrix saleId={sale.id} />
    </TabsContent>
    )}

    {!isRestricted && !isDigitalSale && (
    <TabsContent value="sessions">
      <SessionsTable sale={sale} saleSessions={saleSessions} onCreateSession={onCreateSession} onEditSession={onEditSession} />
    </TabsContent>
    )}

    {!isRestricted && (
    <TabsContent value="deliverables">
      {saleOpportunities.length === 0 ? (
        <p className="text-sm text-muted-foreground italic py-4">No horses entered in this sale</p>
      ) : (
        <DeliverableMatrix
          opportunities={saleOpportunities}
          saleOwnerDeliverables={saleOwnerDeliverables}
          opportunityOwners={opportunityOwners}
          ownerEntities={ownerEntities}
          saleId={sale.id}
        />
      )}
    </TabsContent>
    )}
  </Tabs>
  );
};

const PreSaleTabs = ({
  sale, saleSessions, saleOpportunities, saleOwnerDeliverables,
  opportunityOwners, ownerEntities, onCreateSession, onEditSession, navigate
}) => (
  <Tabs defaultValue={sale.is_digital_sale ? "opportunities" : "sessions"} className="mt-4">
    <TabsList className="flex w-full overflow-x-auto h-auto flex-nowrap scrollbar-none">
      {!sale.is_digital_sale && <TabsTrigger value="sessions">Sessions</TabsTrigger>}
      <TabsTrigger value="opportunities">Sale Opportunities</TabsTrigger>
      <TabsTrigger value="deliverables">Deliverable Matrix</TabsTrigger>
    </TabsList>

    {!sale.is_digital_sale && (
    <TabsContent value="sessions">
      <SessionsTable sale={sale} saleSessions={saleSessions} onCreateSession={onCreateSession} onEditSession={onEditSession} />
    </TabsContent>
    )}

    <TabsContent value="opportunities">
      <OpportunitiesTable
        saleOpportunities={saleOpportunities}
        saleSessions={saleSessions}
        opportunityOwners={opportunityOwners}
        navigate={navigate}
        variant="presale"
        sale={sale}
      />
    </TabsContent>

    <TabsContent value="deliverables">
      {saleOpportunities.length === 0 ? (
        <p className="text-sm text-muted-foreground italic py-4">No horses entered in this sale</p>
      ) : (
        <DeliverableMatrix
          opportunities={saleOpportunities}
          saleOwnerDeliverables={saleOwnerDeliverables}
          opportunityOwners={opportunityOwners}
          ownerEntities={ownerEntities}
          saleId={sale.id}
        />
      )}
    </TabsContent>
  </Tabs>
);

/* --- Kiosk Link Button with QR Dialog --- */
const KioskLinkButton = ({ saleId }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [kioskUrl, setKioskUrl] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState(null);

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Generate token
      const res = await fetch(`${window.location.origin}/api/kiosk/generate-token/${saleId}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      const fullUrl = `${window.location.origin}${data.kiosk_url}`;
      setKioskUrl(fullUrl);

      // Get QR code with full URL
      const qrRes = await fetch(`${window.location.origin}/api/kiosk/qr/${saleId}?base_url=${encodeURIComponent(window.location.origin)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (qrRes.ok) {
        const blob = await qrRes.blob();
        setQrDataUrl(URL.createObjectURL(blob));
      }
    } catch { toast.error('Failed to generate kiosk link'); }
    setLoading(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(kioskUrl);
    toast.success('Kiosk link copied to clipboard');
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={handleOpen}>
        <QrCode className="w-4 h-4 mr-2" /> Kiosk Link
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Self-Service Kiosk</DialogTitle></DialogHeader>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
          ) : (
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">Display this QR code at the sale for viewers to self-submit their cards.</p>
              {qrDataUrl && (
                <div className="flex flex-col items-center gap-2 p-4 bg-white border rounded">
                  <img src={qrDataUrl} alt="Kiosk QR Code" className="w-48 h-48" />
                  <Button variant="outline" size="sm" className="text-xs"
                    onClick={() => {
                      const a = document.createElement('a');
                      a.href = qrDataUrl;
                      a.download = `kiosk-qr-${saleId}.png`;
                      a.click();
                    }}>
                    <Download className="w-3 h-3 mr-1" /> Download QR Code
                  </Button>
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-xs">Kiosk URL</Label>
                <div className="flex gap-2">
                  <Input value={kioskUrl} readOnly className="text-xs" />
                  <Button variant="outline" size="icon" onClick={copyLink} title="Copy"><Copy className="w-4 h-4" /></Button>
                  <Button variant="outline" size="icon" onClick={() => window.open(kioskUrl, '_blank')} title="Open"><ExternalLink className="w-4 h-4" /></Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Viewers scan the QR code on their phone to access the card submission form. No login required.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SaleCard;
